import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ImageOff,
  Layers3,
  Loader2,
  PackagePlus,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

const initialForm = {
  combo_name: "",
  combo_price: "",
  combo_description: "",
  combo_image: "",
  combo_type: "Configurable",
  is_configurable: "Y",
  is_active: "Y",
};

const createInitialGroups = () => [
  {
    localId: "pizza",
    itemSelectionName: "Choose Your Pizza",
    minimumSelection: "1",
    maximumSelection: "5",
    isConfigurable: "Y",
  },
  {
    localId: "burger",
    itemSelectionName: "Choose Your Burger",
    minimumSelection: "1",
    maximumSelection: "5",
    isConfigurable: "N",
  },
  {
    localId: "cold-drink",
    itemSelectionName: "Choose Your Cold Drink",
    minimumSelection: "1",
    maximumSelection: "5",
    isConfigurable: "N",
  },
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const toText = (value) => String(value ?? "").trim();

const ALLOWED_COMBO_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

const ALLOWED_COMBO_IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp"]);

const isAllowedComboImageFile = (file) => {
  if (!file) return false;

  const mimeType = String(file?.type || "")
    .trim()
    .toLowerCase();
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    ?.trim()
    .toLowerCase();

  return (
    ALLOWED_COMBO_IMAGE_TYPES.has(mimeType) ||
    ALLOWED_COMBO_IMAGE_EXTENSIONS.has(extension)
  );
};

const getSelectionRule = (group = {}) => {
  const min = String(
    group?.minimum_selection ?? group?.minimumSelection ?? "0",
  );
  const max = String(
    group?.maximum_selection ?? group?.maximumSelection ?? "0",
  );

  if (min === max) return `Choose exactly ${min}`;
  return `Choose ${min} to ${max}`;
};

const getPriceLabel = (price = {}) => {
  if (price?.formatted_price) return String(price.formatted_price);

  const value =
    price?.price ??
    price?.selling_price ??
    price?.takeaway_price ??
    price?.takewayPrice;

  if (value !== null && value !== undefined && value !== "") {
    return `₹${value}`;
  }

  return "—";
};

const ComboTab = ({
  comboList = [],
  searchText = "",
  comboOfferSaving = false,
  comboOfferUpdating = false,
  bhandareImageUploading = false,
  optionGroupSaving = false,
  optionGroupUpdating = false,
  comboConfigurableSaving = false,
  comboConfigurableUpdating = false,
  catalogV3Loading = false,

  insertComboOffer,
  updateComboOffer,
  uploadComboImage,
  getComboOffer,
  insertComboOptionGroupDetails,
  updateComboOptionGroupItems,
  getOptionGroupItems,
  insertComboConfigurable,
  updateComboConfigurable,
  getCatalogItemsV3,

  userID = "",
  catalogueItemsFallback = [],

  onMasterRefresh,
}) => {
  const [form, setForm] = useState(initialForm);

  // Workflow:
  // 1 = create combo
  // 2 = option groups
  // 3 = map items
  const [workflowStep, setWorkflowStep] = useState(1);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [createdCombo, setCreatedCombo] = useState(null);

  const [draftGroups, setDraftGroups] = useState(createInitialGroups);
  const [savedGroups, setSavedGroups] = useState([]);
  const [groupSelections, setGroupSelections] = useState({});

  // Latest getComboOffer response.
  // Used during the creation flow before master catalogue is refreshed.
  const [getComboOfferResponse, setGetComboOfferResponse] = useState([]);

  // Final getCatalogueItemsMaster comboList.
  // This has the complete optionGroup -> items -> prices/customizations data.
  const [masterComboResponse, setMasterComboResponse] = useState([]);

  const [expandedComboId, setExpandedComboId] = useState(null);

  // Persist current workflow across refresh and Catalogue tab switching.
  const hydratedRef = useRef(false);

  const storageKey = useMemo(
    () => `pos_combo_old_ui_progress_${String(userID || "guest")}`,
    [userID],
  );

  // Item selector modal
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);
  const [itemSearchText, setItemSearchText] = useState("");

  // Combo image used during create flow. The upload API expects a full Data URL.
  const [createImageData, setCreateImageData] = useState("");

  // Existing combo edit modal.
  const [editingCombo, setEditingCombo] = useState(null);
  const [editForm, setEditForm] = useState({
    combo_name: "",
    combo_price: "",
    combo_description: "",
    combo_image: "",
    is_active: "Y",
  });
  const [editImageData, setEditImageData] = useState("");
  const [statusBusyKey, setStatusBusyKey] = useState("");

  // updateComboOptionGroupItems UI state.
  // Master catalogue does not always return the freshly updated is_active value,
  // so keep the successful status locally for an immediate and visible UI update.
  const [groupStatusOverrides, setGroupStatusOverrides] = useState({});
  const [groupStatusChanges, setGroupStatusChanges] = useState({});
  const groupStatusInitialSaveRef = useRef(true);

  const groupStatusStorageKey = useMemo(
    () => `pos_combo_group_status_${String(userID || "guest")}`,
    [userID],
  );

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(groupStatusStorageKey);
      if (!raw) return;

      const saved = JSON.parse(raw);

      if (saved?.overrides && typeof saved.overrides === "object") {
        setGroupStatusOverrides(saved.overrides);
      }

      if (saved?.changes && typeof saved.changes === "object") {
        setGroupStatusChanges(saved.changes);
      }
    } catch (error) {
      console.error("Combo group status restore error:", error);
    }
  }, [groupStatusStorageKey]);

  useEffect(() => {
    // Skip the first save so a stored status is not overwritten by the empty
    // initial React state before the restore effect has applied it.
    if (groupStatusInitialSaveRef.current) {
      groupStatusInitialSaveRef.current = false;
      return;
    }

    try {
      sessionStorage.setItem(
        groupStatusStorageKey,
        JSON.stringify({
          overrides: groupStatusOverrides,
          changes: groupStatusChanges,
        }),
      );
    } catch (error) {
      console.error("Combo group status save error:", error);
    }
  }, [groupStatusStorageKey, groupStatusOverrides, groupStatusChanges]);

  // =========================================================
  // RESTORE OLD UI PROGRESS
  // =========================================================

  useEffect(() => {
    hydratedRef.current = false;

    try {
      const raw = localStorage.getItem(storageKey);

      if (!raw) {
        hydratedRef.current = true;
        return;
      }

      const saved = JSON.parse(raw);

      if (saved?.form && typeof saved.form === "object") {
        setForm({
          ...initialForm,
          ...saved.form,
        });
      }

      const savedStep = Number(saved?.workflowStep);

      if (savedStep >= 1 && savedStep <= 3) {
        setWorkflowStep(savedStep);
      }

      if (saved?.createdCombo && typeof saved.createdCombo === "object") {
        setCreatedCombo(saved.createdCombo);
      }

      if (Array.isArray(saved?.draftGroups)) {
        setDraftGroups(saved.draftGroups);
      }

      if (Array.isArray(saved?.savedGroups)) {
        setSavedGroups(saved.savedGroups);
      }

      if (saved?.groupSelections && typeof saved.groupSelections === "object") {
        setGroupSelections(saved.groupSelections);
      }

      if (Array.isArray(saved?.getComboOfferResponse)) {
        setGetComboOfferResponse(saved.getComboOfferResponse);
      }

      if (Array.isArray(saved?.masterComboResponse)) {
        setMasterComboResponse(saved.masterComboResponse);
      }

      if (saved?.expandedComboId) {
        setExpandedComboId(String(saved.expandedComboId));
      }
    } catch (error) {
      console.error("Combo old UI progress restore error:", error);
    } finally {
      hydratedRef.current = true;
    }
  }, [storageKey]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          form,
          workflowStep,
          createdCombo,
          draftGroups,
          savedGroups,
          groupSelections,
          getComboOfferResponse,
          masterComboResponse,
          expandedComboId,
        }),
      );
    } catch (error) {
      console.error("Combo old UI progress save error:", error);
    }
  }, [
    storageKey,
    form,
    workflowStep,
    createdCombo,
    draftGroups,
    savedGroups,
    groupSelections,
    getComboOfferResponse,
    masterComboResponse,
    expandedComboId,
  ]);

  const safeComboList = useMemo(
    () => (Array.isArray(comboList) ? comboList : []),
    [comboList],
  );

  const displayComboList = useMemo(() => {
    // After final getCatalogueItemsMaster, always prefer its full comboList.
    if (Array.isArray(masterComboResponse) && masterComboResponse.length > 0) {
      return masterComboResponse;
    }

    // During create flow, show getComboOffer immediately.
    if (
      Array.isArray(getComboOfferResponse) &&
      getComboOfferResponse.length > 0
    ) {
      return getComboOfferResponse;
    }

    return safeComboList;
  }, [masterComboResponse, getComboOfferResponse, safeComboList]);

  const extractMasterComboList = (response) => {
    const candidates = [
      response?.comboList,
      response?.response?.comboList,
      response?.data?.comboList,
      response?.data?.response?.comboList,
      response?.result?.comboList,
      response?.response?.response?.comboList,
    ];

    const found = candidates.find(Array.isArray);

    return Array.isArray(found) ? found : [];
  };

  const refreshMasterComboList = async () => {
    const masterResult = await onMasterRefresh?.();
    const masterCombos = extractMasterComboList(masterResult);

    if (masterCombos.length > 0) {
      setMasterComboResponse(masterCombos);
      setGetComboOfferResponse([]);
    }

    return masterCombos;
  };

  const filteredCombos = useMemo(() => {
    const query = String(searchText || "")
      .trim()
      .toLowerCase();

    if (!query) return displayComboList;

    return displayComboList.filter((combo) => {
      const text = [
        combo?.combo_name,
        combo?.combo_description,
        combo?.combo_type,
        combo?.combo_price,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");

      return text.includes(query);
    });
  }, [displayComboList, searchText]);

  const configurableComboCount = useMemo(
    () =>
      displayComboList.filter(
        (combo) => String(combo?.is_configurable || "").toUpperCase() === "Y",
      ).length,
    [displayComboList],
  );

  // ---------------------------------------------------------
  // CURRENT CATALOGUE ITEMS
  // ---------------------------------------------------------

  const flatCatalogueItems = useMemo(() => {
    const source = asArray(catalogueItemsFallback);

    const rows = source.flatMap((category) => {
      if (Array.isArray(category?.menu_items)) {
        return category.menu_items.map((item) => ({
          ...item,
          __categoryName:
            category?.category_name ||
            category?.categoryName ||
            category?.name ||
            "",
        }));
      }

      if (Array.isArray(category?.items)) {
        return category.items.map((item) => ({
          ...item,
          __categoryName:
            category?.category_name ||
            category?.categoryName ||
            category?.name ||
            "",
        }));
      }

      return [category];
    });

    const seen = new Set();

    return rows.filter((item) => {
      const itemId = toText(
        item?.item_id ?? item?.itemId ?? item?.pk_item_id ?? item?.id,
      );

      if (!itemId || seen.has(itemId)) return false;

      seen.add(itemId);
      return true;
    });
  }, [catalogueItemsFallback]);

  const getItemId = (item) =>
    toText(item?.item_id ?? item?.itemId ?? item?.pk_item_id ?? item?.id);

  const getItemName = (item) =>
    toText(
      item?.item_name ??
        item?.itemName ??
        item?.productName ??
        item?.name ??
        "Unnamed item",
    );

  const getItemImage = (item) =>
    item?.photo || item?.itemImage || item?.image || item?.item_image || "";

  const getItemPrices = (item) =>
    Array.isArray(item?.prices)
      ? item.prices
      : Array.isArray(item?.priceList)
        ? item.priceList
        : [];

  const filteredSelectableItems = useMemo(() => {
    const query = String(itemSearchText || "")
      .trim()
      .toLowerCase();

    if (!query) return flatCatalogueItems;

    return flatCatalogueItems.filter((item) => {
      const prices = getItemPrices(item);

      const text = [
        getItemName(item),
        getItemId(item),
        item?.__categoryName,
        item?.sub_category,
        ...prices.map(
          (price) =>
            price?.size || price?.variantName || price?.variant_name || "",
        ),
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");

      return text.includes(query);
    });
  }, [flatCatalogueItems, itemSearchText]);

  // ---------------------------------------------------------
  // STEP 1
  // insertComboOffer -> getComboOffer -> resolve fk combo ID
  // ---------------------------------------------------------

  const resolveCreatedCombo = (offers, submittedForm) => {
    const safeOffers = asArray(offers);

    const sameName = safeOffers.filter(
      (combo) =>
        toText(combo?.combo_name).toLowerCase() ===
        toText(submittedForm?.combo_name).toLowerCase(),
    );

    if (!sameName.length) return null;

    const exactMatches = sameName.filter((combo) => {
      const samePrice =
        toText(combo?.combo_price) === toText(submittedForm?.combo_price);

      const sameDescription =
        toText(combo?.combo_description).toLowerCase() ===
        toText(submittedForm?.combo_description).toLowerCase();

      const sameType =
        toText(combo?.combo_type).toLowerCase() ===
        toText(submittedForm?.combo_type).toLowerCase();

      const sameConfigurable =
        toText(combo?.is_configurable).toUpperCase() ===
        toText(submittedForm?.is_configurable).toUpperCase();

      return samePrice && sameDescription && sameType && sameConfigurable;
    });

    const candidates = exactMatches.length > 0 ? exactMatches : sameName;

    return [...candidates].sort(
      (a, b) => Number(b?.combo_id || 0) - Number(a?.combo_id || 0),
    )[0];
  };

  // IMPORTANT: keep the selected file bytes/format unchanged.
  // PNG stays PNG, JPG/JPEG stays JPEG, WEBP stays WEBP.
  // No canvas, resize, compression, or WEBP conversion is done here.
  const readFileAsOriginalDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () =>
        reject(reader.error || new Error("File read failed"));
      reader.readAsDataURL(file);
    });

  const handleCreateImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedComboImageFile(file)) {
      toast.error("Only PNG, JPG, JPEG and WEBP images are allowed");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsOriginalDataUrl(file);
      setCreateImageData(dataUrl);
    } catch (error) {
      console.error("Create combo image read error:", error);
      toast.error("Unable to read selected image");
    }
  };

  const openEditCombo = (combo) => {
    setEditingCombo(combo);
    setEditForm({
      combo_name: toText(combo?.combo_name),
      combo_price: toText(combo?.combo_price),
      combo_description: toText(combo?.combo_description),
      combo_image: toText(combo?.combo_image),
      is_active:
        toText(combo?.is_active || "Y").toUpperCase() === "N" ? "N" : "Y",
    });
    setEditImageData("");
  };

  const closeEditCombo = () => {
    if (comboOfferUpdating || bhandareImageUploading) return;
    setEditingCombo(null);
    setEditImageData("");
  };

  const handleEditImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedComboImageFile(file)) {
      toast.error("Only PNG, JPG, JPEG and WEBP images are allowed");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsOriginalDataUrl(file);
      setEditImageData(dataUrl);
    } catch (error) {
      console.error("Edit combo image read error:", error);
      toast.error("Unable to read selected image");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCombo = async (event) => {
    event.preventDefault();

    if (!form.combo_name.trim()) {
      toast.error("Please enter combo name");
      return;
    }

    if (!form.combo_price.trim()) {
      toast.error("Please enter combo price");
      return;
    }

    setWorkflowBusy(true);

    try {
      // Upload combo image first when an image is selected.
      let comboImageUrl = toText(form.combo_image);

      if (createImageData) {
        const uploadResult = await uploadComboImage?.({
          item_name: form.combo_name.trim(),
          image_data: createImageData,
          type: "IMG",
        });

        if (!uploadResult?.image_url) {
          return;
        }

        comboImageUrl = toText(uploadResult.image_url);
      }

      // FLOW 1
      const inserted = await insertComboOffer?.({
        combo_name: form.combo_name.trim(),
        combo_price: form.combo_price.trim(),
        combo_description: form.combo_description.trim(),
        combo_image: comboImageUrl,
        combo_type: form.combo_type.trim(),
        is_configurable: form.is_configurable,
        is_active: form.is_active,
      });

      if (!inserted) return;

      const offers = await getComboOffer?.(form.combo_name.trim(), {
        force: true,
      });

      setMasterComboResponse([]);

      // Map getComboOffer API response in the SAME old right-side Combo List UI.
      setGetComboOfferResponse(Array.isArray(offers) ? offers : []);

      const resolvedCombo = resolveCreatedCombo(offers, form);

      if (!resolvedCombo?.combo_id) {
        toast.error("Combo inserted but new combo ID could not be resolved");
        return;
      }

      setCreatedCombo(resolvedCombo);
      setDraftGroups(createInitialGroups());
      setSavedGroups([]);
      setGroupSelections({});
      setCreateImageData("");
      setWorkflowStep(2);

      // toast.success(
      //   `Combo ID ${resolvedCombo.combo_id} selected. Add option groups.`,
      // );
    } catch (error) {
      console.error("Create combo flow error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start combo setup",
      );
    } finally {
      setWorkflowBusy(false);
    }
  };

  // ---------------------------------------------------------
  // STEP 2
  // insertComboOptionGroupDetails -> getOptionGroupItems
  // ---------------------------------------------------------

  const updateDraftGroup = (localId, field, value) => {
    setDraftGroups((prev) =>
      prev.map((group) =>
        group.localId === localId
          ? {
              ...group,
              [field]: value,
            }
          : group,
      ),
    );
  };

  const addDraftGroup = () => {
    setDraftGroups((prev) => [
      ...prev,
      {
        localId: `group-${Date.now()}-${prev.length}`,
        itemSelectionName: "",
        minimumSelection: "1",
        maximumSelection: "1",
        // Default requested by user.
        isConfigurable: "N",
      },
    ]);
  };

  const removeDraftGroup = (localId) => {
    setDraftGroups((prev) => prev.filter((group) => group.localId !== localId));
  };

  const validateGroups = () => {
    if (!draftGroups.length) {
      toast.error("Please add at least one option group");
      return false;
    }

    for (const group of draftGroups) {
      if (!toText(group.itemSelectionName)) {
        toast.error("Option group name is required");
        return false;
      }

      const minimum = Number(group.minimumSelection);
      const maximum = Number(group.maximumSelection);

      if (
        !Number.isFinite(minimum) ||
        !Number.isFinite(maximum) ||
        minimum < 0 ||
        maximum < 0
      ) {
        toast.error(`Invalid selection limit for ${group.itemSelectionName}`);
        return false;
      }

      if (maximum < minimum) {
        toast.error(
          `Maximum selection cannot be less than minimum for ${group.itemSelectionName}`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSaveOptionGroups = async () => {
    if (!createdCombo?.combo_id) {
      toast.error("Resolved combo ID missing");
      return;
    }

    if (!validateGroups()) return;

    setWorkflowBusy(true);

    try {
      // FLOW 3
      // Add groups sequentially so API order remains predictable.
      for (const group of draftGroups) {
        const success = await insertComboOptionGroupDetails?.({
          fkComboId: String(createdCombo.combo_id),
          itemSelectionName: group.itemSelectionName.trim(),
          minimumSelection: String(group.minimumSelection),
          maximumSelection: String(group.maximumSelection),
          // N is preselected; user can change it to Y.
          isConfigurable: group.isConfigurable || "N",
        });

        if (!success) {
          toast.error(`Stopped while creating ${group.itemSelectionName}`);
          return;
        }
      }

      // FLOW 4
      const fetchedGroups = await getOptionGroupItems?.(
        String(createdCombo.combo_id),
      );

      if (!Array.isArray(fetchedGroups)) {
        toast.error("Option groups created but group IDs were not returned");
        return;
      }

      const wantedNames = new Set(
        draftGroups.map((group) =>
          toText(group.itemSelectionName).toLowerCase(),
        ),
      );

      const matchingGroups = fetchedGroups.filter((group) =>
        wantedNames.has(
          toText(
            group?.item_selection_name || group?.itemSelectionName,
          ).toLowerCase(),
        ),
      );

      const finalGroups =
        matchingGroups.length > 0 ? matchingGroups : fetchedGroups;

      setSavedGroups(finalGroups);
      setWorkflowStep(3);

      // toast.success("Option groups created. Now select items for each group.");
    } catch (error) {
      console.error("Save option groups flow error:", error);
      toast.error("Unable to create option groups");
    } finally {
      setWorkflowBusy(false);
    }
  };

  const getGroupId = (group) =>
    toText(
      group?.pk_option_group_item_id ??
        group?.option_group_item_id ??
        group?.id,
    );

  const getGroupName = (group) =>
    toText(
      group?.item_selection_name ??
        group?.itemSelectionName ??
        group?.groupTitle ??
        "Option Group",
    );

  const openItemSelector = (group) => {
    const groupId = getGroupId(group);

    if (!groupId) {
      toast.error("Option group item ID missing");
      return;
    }

    setTargetGroup(group);
    setItemSearchText("");
    setShowItemSelector(true);
  };

  const closeItemSelector = () => {
    setShowItemSelector(false);
    setTargetGroup(null);
    setItemSearchText("");
  };

  const toggleGroupItem = (groupId, itemId) => {
    const finalGroupId = toText(groupId);
    const finalItemId = toText(itemId);

    if (!finalGroupId || !finalItemId) return;

    setGroupSelections((prev) => {
      const current = Array.isArray(prev[finalGroupId])
        ? prev[finalGroupId]
        : [];

      return {
        ...prev,
        [finalGroupId]: current.includes(finalItemId)
          ? current.filter((id) => id !== finalItemId)
          : [...current, finalItemId],
      };
    });
  };

  const handleFinishComboSetup = async () => {
    if (!createdCombo?.combo_id) {
      toast.error("Combo ID missing");
      return;
    }

    if (!savedGroups.length) {
      toast.error("Option groups missing");
      return;
    }

    // Require item selection for every group.
    for (const group of savedGroups) {
      const groupId = getGroupId(group);
      const selectedIds = asArray(groupSelections[groupId]);

      if (!selectedIds.length) {
        toast.error(`Please select items for ${getGroupName(group)}`);
        return;
      }
    }

    setWorkflowBusy(true);

    try {
      for (const group of savedGroups) {
        const groupId = getGroupId(group);
        const selectedIds = asArray(groupSelections[groupId]);

        const success = await insertComboConfigurable?.({
          user_id: String(userID),
          combo_id: String(createdCombo.combo_id),
          option_group_item_id: groupId,
          item_id: selectedIds,
        });

        if (!success) {
          toast.error(`Stopped while saving items for ${getGroupName(group)}`);
          return;
        }
      }

      const v3Result = await getCatalogItemsV3?.({
        userid: String(userID),
        orderType: "takeaway",
        platform: "pos",
      });

      if (!v3Result) {
        toast.error("Combo saved, but v3 catalogue refresh failed");
        return;
      }

      const masterResult = await onMasterRefresh?.();

      const masterCombos = extractMasterComboList(masterResult);

      console.log("FLOW 7 - MAPPED MASTER COMBO LIST:", masterCombos);

      if (masterCombos.length > 0) {
        setMasterComboResponse(masterCombos);
      }

      setGetComboOfferResponse([]);
      const completedComboId = String(createdCombo.combo_id);

      setExpandedComboId(completedComboId);

      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.error("Combo progress clear error:", error);
      }

      setForm(initialForm);
      setCreatedCombo(null);
      setDraftGroups(createInitialGroups());
      setSavedGroups([]);
      setGroupSelections({});
      setWorkflowStep(1);
    } catch (error) {
      console.error("Finish combo setup error:", error);
      toast.error("Unable to complete combo setup");
    } finally {
      setWorkflowBusy(false);
    }
  };

  const handleUpdateCombo = async (event) => {
    event?.preventDefault?.();

    if (!editingCombo?.combo_id) {
      toast.error("Combo ID missing");
      return;
    }

    if (!editForm.combo_name.trim()) {
      toast.error("Combo name is required");
      return;
    }

    let comboImageUrl = toText(editForm.combo_image);

    try {
      if (editImageData) {
        const uploadResult = await uploadComboImage?.({
          item_name: editForm.combo_name.trim(),
          image_data: editImageData,
          type: "IMG",
        });

        if (!uploadResult?.image_url) return;
        comboImageUrl = toText(uploadResult.image_url);
      }

      const success = await updateComboOffer?.({
        combo_id: String(editingCombo.combo_id),
        combo_name: editForm.combo_name.trim(),
        combo_price: editForm.combo_price.trim(),
        combo_description: editForm.combo_description.trim(),
        combo_image: comboImageUrl,
        is_active: editForm.is_active,
      });

      if (!success) return;

      await refreshMasterComboList();
      setEditingCombo(null);
      setEditImageData("");
    } catch (error) {
      console.error("Update combo UI flow error:", error);
      toast.error("Unable to update combo");
    }
  };

  const getGroupStatusKey = (comboId, groupId) =>
    `${toText(comboId)}:${toText(groupId)}`;

  const getEffectiveGroupStatus = (comboId, group) => {
    const groupId = getGroupId(group);
    const statusKey = getGroupStatusKey(comboId, groupId);
    const override = groupStatusOverrides[statusKey];

    if (override === "Y" || override === "N") {
      return override;
    }

    return toText(group?.is_active || "Y").toUpperCase() === "N" ? "N" : "Y";
  };

  const handleToggleOptionGroup = async (comboId, group) => {
    const groupId = getGroupId(group);

    if (!groupId) {
      toast.error("Option group item ID missing");
      return;
    }

    const statusKey = getGroupStatusKey(comboId, groupId);
    const currentStatus = getEffectiveGroupStatus(comboId, group);
    const nextStatus = currentStatus === "Y" ? "N" : "Y";
    const busyKey = `group:${groupId}`;

    // Optimistic UI: show the new state immediately instead of waiting for a
    // master refresh that may not contain is_active.
    setGroupStatusOverrides((prev) => ({
      ...prev,
      [statusKey]: nextStatus,
    }));

    setStatusBusyKey(busyKey);

    try {
      const success = await updateComboOptionGroupItems?.({
        option_group_item_id: groupId,
        is_active: nextStatus,
      });

      if (!success) {
        setGroupStatusOverrides((prev) => ({
          ...prev,
          [statusKey]: currentStatus,
        }));
        return;
      }

      setGroupStatusChanges((prev) => ({
        ...prev,
        [statusKey]: {
          from: currentStatus,
          to: nextStatus,
          groupName: getGroupName(group),
          updatedAt: Date.now(),
        },
      }));

      // Refresh other combo details, but keep the successful status override so
      // the UI cannot jump back to the old/default value.
      await refreshMasterComboList();

      toast.success(
        `${getGroupName(group)}: ${
          currentStatus === "Y" ? "Active" : "Inactive"
        } → ${nextStatus === "Y" ? "Active" : "Inactive"}`,
      );
    } catch (error) {
      setGroupStatusOverrides((prev) => ({
        ...prev,
        [statusKey]: currentStatus,
      }));

      console.error("Option group status UI update error:", error);
      toast.error("Unable to update option group status");
    } finally {
      setStatusBusyKey("");
    }
  };

  const handleToggleComboItem = async (comboId, item) => {
    const itemId = getItemId(item);
    if (!comboId || !itemId) {
      toast.error("Combo ID or item ID missing");
      return;
    }

    const currentActive =
      toText(
        item?.is_active ??
          item?.active_status ??
          item?.combo_is_active ??
          item?.mapping_is_active ??
          "Y",
      ).toUpperCase() !== "N";
    const nextActive = currentActive ? "N" : "Y";
    const busyKey = `item:${comboId}:${itemId}`;

    setStatusBusyKey(busyKey);

    try {
      const success = await updateComboConfigurable?.({
        combo_id: String(comboId),
        item_id: itemId,
        is_active: nextActive,
      });

      if (!success) return;
      await refreshMasterComboList();
    } finally {
      setStatusBusyKey("");
    }
  };

  const resetWorkflow = () => {
    if (
      workflowBusy ||
      comboOfferSaving ||
      comboOfferUpdating ||
      bhandareImageUploading ||
      optionGroupSaving ||
      optionGroupUpdating ||
      comboConfigurableSaving ||
      comboConfigurableUpdating ||
      catalogV3Loading
    ) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Combo progress clear error:", error);
    }

    setForm(initialForm);
    setCreateImageData("");
    setCreatedCombo(null);
    setDraftGroups(createInitialGroups());
    setSavedGroups([]);
    setGroupSelections({});
    setGetComboOfferResponse([]);
    setMasterComboResponse([]);
    setWorkflowStep(1);
    closeItemSelector();
  };

  const stepBusy =
    workflowBusy ||
    comboOfferSaving ||
    comboOfferUpdating ||
    bhandareImageUploading ||
    optionGroupSaving ||
    optionGroupUpdating ||
    comboConfigurableSaving ||
    comboConfigurableUpdating ||
    catalogV3Loading;

  return (
    <>
      <div className="grid min-h-full grid-cols-1 gap-5 pb-5 xl:grid-cols-[430px_minmax(0,1fr)]">
        {/* =====================================================
            LEFT - CREATE COMBO WORKFLOW
           ===================================================== */}
        <div className="h-fit rounded-2xl border bg-white shadow-sm xl:sticky xl:top-0">
          {/* Steps */}
          <div className="border-b p-5">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((step) => {
                const done = workflowStep > step;
                const active = workflowStep === step;

                return (
                  <React.Fragment key={step}>
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        done
                          ? "bg-green-600 text-white"
                          : active
                            ? "bg-green-100 text-green-700 ring-2 ring-green-500"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {done ? <Check size={15} /> : step}
                    </div>

                    {step < 3 && (
                      <div
                        className={`h-0.5 flex-1 ${
                          workflowStep > step ? "bg-green-500" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="mt-3 grid grid-cols-3 text-center text-[10px] font-semibold">
              <span
                className={
                  workflowStep === 1 ? "text-green-700" : "text-gray-500"
                }
              >
                Combo
              </span>
              <span
                className={
                  workflowStep === 2 ? "text-green-700" : "text-gray-500"
                }
              >
                Groups
              </span>
              <span
                className={
                  workflowStep === 3 ? "text-green-700" : "text-gray-500"
                }
              >
                Items
              </span>
            </div>
          </div>

          {/* STEP 1 */}
          {workflowStep === 1 && (
            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-100 text-green-700">
                  <PackagePlus size={17} />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Create Combo
                  </h3>
                </div>
              </div>

              <form onSubmit={handleCreateCombo} className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Name
                  </label>
                  <input
                    name="combo_name"
                    value={form.combo_name}
                    onChange={handleChange}
                    placeholder="e.g. Birthday Combo"
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Price
                  </label>
                  <input
                    name="combo_price"
                    value={form.combo_price}
                    onChange={handleChange}
                    placeholder="499"
                    inputMode="decimal"
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="combo_description"
                    value={form.combo_description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Combo details..."
                    className="w-full resize-none rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Image
                  </label>

                  <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed bg-gray-50 p-3 hover:border-green-400 hover:bg-green-50/40">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
                      {createImageData || form.combo_image ? (
                        <img
                          src={createImageData || form.combo_image}
                          alt="Combo preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Upload size={18} className="text-gray-400" />
                      )}
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-700">
                        {createImageData
                          ? "Image selected"
                          : "Choose combo image"}
                      </p>
                      <p className="mt-0.5 text-[10px] text-gray-500">
                        PNG/JPG/JPEG/WEBP sent in original format
                      </p>
                    </div>

                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                      onChange={handleCreateImageChange}
                      disabled={stepBusy}
                      className="hidden"
                    />
                  </label>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Type
                  </label>
                  <select
                    name="combo_type"
                    value={form.combo_type}
                    onChange={handleChange}
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500"
                  >
                    <option value="Configurable">Configurable</option>
                    <option value="Fixed">Fixed</option>
                    <option value="Veg">Veg</option>
                    <option value="Non Veg">Non Veg</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Configurable
                    </label>
                    <select
                      name="is_configurable"
                      value={form.is_configurable}
                      onChange={handleChange}
                      className="h-10 w-full rounded-lg border bg-white px-3 text-sm"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Active
                    </label>
                    <select
                      name="is_active"
                      value={form.is_active}
                      onChange={handleChange}
                      className="h-10 w-full rounded-lg border bg-white px-3 text-sm"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={stepBusy}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {stepBusy ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  {stepBusy ? "Creating..." : "Create & Continue"}
                </button>
              </form>
            </div>
          )}

          {/* STEP 2 */}
          {workflowStep === 2 && (
            <div className="p-5">
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="mt-1 text-sm font-bold text-green-900">
                  {createdCombo?.combo_name}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Option Groups
                </h3>
              </div>

              <div className="space-y-3">
                {draftGroups.map((group, index) => (
                  <div
                    key={group.localId}
                    className="rounded-xl border bg-gray-50 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">
                        Group {index + 1}
                      </span>

                      {draftGroups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDraftGroup(group.localId)}
                          disabled={stepBusy}
                          className="text-red-500 hover:text-red-600 disabled:opacity-40"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    <input
                      value={group.itemSelectionName}
                      onChange={(event) =>
                        updateDraftGroup(
                          group.localId,
                          "itemSelectionName",
                          event.target.value,
                        )
                      }
                      placeholder="Choose Your Pizza"
                      className="h-9 w-full rounded-lg border bg-white px-3 text-xs outline-none focus:border-green-500"
                    />

                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Min
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={group.minimumSelection}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "minimumSelection",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Max
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={group.maximumSelection}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "maximumSelection",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Configurable
                        </label>
                        <select
                          value={group.isConfigurable}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "isConfigurable",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        >
                          <option value="N">No</option>
                          <option value="Y">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addDraftGroup}
                disabled={stepBusy}
                className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-green-300 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
              >
                <Plus size={14} />
                Add Another Group
              </button>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={resetWorkflow}
                  disabled={stepBusy}
                  className="h-10 rounded-lg border bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSaveOptionGroups}
                  disabled={stepBusy}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg bg-green-600 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {stepBusy && <Loader2 size={14} className="animate-spin" />}
                  Save Groups
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {workflowStep === 3 && (
            <div className="p-5">
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="text-[10px] font-semibold uppercase text-green-600">
                  Combo #{createdCombo?.combo_id}
                </p>
                <p className="mt-1 text-sm font-bold text-green-900">
                  {createdCombo?.combo_name}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Map Items
                </h3>
              </div>

              <div className="space-y-3">
                {savedGroups.map((group) => {
                  const groupId = getGroupId(group);
                  const selectedIds = asArray(groupSelections[groupId]);

                  return (
                    <div
                      key={groupId || getGroupName(group)}
                      className="rounded-xl border bg-gray-50 p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-gray-900">
                            {getGroupName(group)}
                          </p>

                          <p className="mt-1 text-[10px] text-gray-500">
                            Group #{groupId} · {getSelectionRule(group)}
                          </p>
                        </div>

                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-bold ${
                            selectedIds.length > 0
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {selectedIds.length} selected
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => openItemSelector(group)}
                        disabled={stepBusy}
                        className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-green-200 bg-white text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
                      >
                        <Plus size={14} />
                        Select Items
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={resetWorkflow}
                  disabled={stepBusy}
                  className="h-10 rounded-lg border bg-white text-xs font-bold text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleFinishComboSetup}
                  disabled={stepBusy}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg bg-green-600 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {stepBusy && <Loader2 size={14} className="animate-spin" />}
                  Finish Combo
                </button>
              </div>
            </div>
          )}
        </div>

        {/* =====================================================
            RIGHT - EXISTING COMBOS
           ===================================================== */}
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-100 text-green-700">
                <Layers3 size={17} />
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Combo List
                </h3>
                <p className="text-[11px] text-gray-500">
                  {displayComboList.length} combos · {configurableComboCount}{" "}
                  configurable
                  {masterComboResponse.length > 0
                    ? " · catalogue master response"
                    : getComboOfferResponse.length > 0
                      ? " · getComboOffer response"
                      : ""}
                </p>
              </div>
            </div>
          </div>

          {filteredCombos.length === 0 ? (
            <div className="rounded-2xl border bg-white p-10 text-center text-sm text-gray-500">
              No combos found.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCombos.map((combo) => {
                const comboId = toText(combo?.combo_id);

                const isExpanded = expandedComboId === comboId;

                const groups = Array.isArray(combo?.optionGroup)
                  ? combo.optionGroup
                  : [];

                const comboActive =
                  toText(combo?.is_active || "Y").toUpperCase() !== "N";

                return (
                  <div
                    key={comboId || combo?.combo_name}
                    className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                  >
                    <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center">
                      <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl border bg-gray-50 lg:w-32">
                        {combo?.combo_image ? (
                          <img
                            src={combo.combo_image}
                            alt={combo?.combo_name || "Combo"}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-gray-300">
                            <ImageOff size={24} />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-semibold text-gray-900">
                            {combo?.combo_name || "Unnamed Combo"}
                          </h4>

                          <span className="rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-semibold text-green-700">
                            #{comboId || "—"}
                          </span>

                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                              comboActive
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-red-50 text-red-600"
                            }`}
                          >
                            {comboActive ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span>{combo?.combo_type || "—"}</span>
                          <span className="font-semibold text-gray-900">
                            ₹{combo?.combo_price || "0"}
                          </span>
                          <span>
                            {groups.length} option group
                            {groups.length === 1 ? "" : "s"}
                          </span>
                        </div>

                        {combo?.combo_description && (
                          <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-600">
                            {combo.combo_description}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openEditCombo(combo)}
                          disabled={
                            comboOfferUpdating || bhandareImageUploading
                          }
                          className="flex h-9 items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                        >
                          <Pencil size={14} />
                          Edit Combo
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setExpandedComboId((current) =>
                              current === comboId ? null : comboId,
                            )
                          }
                          className="flex h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          {isExpanded ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )}
                          {isExpanded ? "Hide Details" : "View Details"}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t bg-gray-50/70 p-4">
                        {groups.length === 0 ? (
                          <p className="text-xs text-gray-500">
                            No option groups available.
                          </p>
                        ) : (
                          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] items-start gap-3">
                            {groups.map((group) => {
                              const groupId = toText(
                                group?.pk_option_group_item_id ??
                                  group?.option_group_item_id,
                              );

                              const items = asArray(group?.items);
                              const groupStatusKey = getGroupStatusKey(
                                comboId,
                                groupId,
                              );
                              const groupStatus = getEffectiveGroupStatus(
                                comboId,
                                group,
                              );
                              const groupActive = groupStatus === "Y";
                              const groupChange =
                                groupStatusChanges[groupStatusKey] || null;
                              const groupBusy =
                                statusBusyKey === `group:${groupId}`;

                              return (
                                <div
                                  key={groupId || getGroupName(group)}
                                  className="min-w-0 rounded-xl border bg-white p-3 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p
                                        className="truncate text-xs font-semibold text-gray-900"
                                        title={getGroupName(group)}
                                      >
                                        {getGroupName(group)}
                                      </p>

                                      <p className="mt-0.5 text-[10px] text-gray-500">
                                        #{groupId || "—"} ·{" "}
                                        {getSelectionRule(group)}
                                      </p>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-1.5">
                                      <span
                                        className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${
                                          groupActive
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-600"
                                        }`}
                                      >
                                        {groupActive ? "Active" : "Inactive"}
                                      </span>

                                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-semibold text-gray-600">
                                        {items.length} item
                                        {items.length === 1 ? "" : "s"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Clear updateComboOptionGroupItems UI */}
                                  <div
                                    className={`mt-3 rounded-lg border p-2.5 ${
                                      groupActive
                                        ? "border-green-200 bg-green-50/70"
                                        : "border-red-200 bg-red-50/70"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                                          Option Group Status
                                        </p>
                                        <p
                                          className={`mt-0.5 text-xs font-bold ${
                                            groupActive
                                              ? "text-green-700"
                                              : "text-red-600"
                                          }`}
                                        >
                                          Currently{" "}
                                          {groupActive ? "Active" : "Inactive"}
                                        </p>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleToggleOptionGroup(
                                            comboId,
                                            group,
                                          )
                                        }
                                        disabled={
                                          groupBusy ||
                                          optionGroupUpdating ||
                                          statusBusyKey !== ""
                                        }
                                        className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                                          groupActive
                                            ? "bg-green-600"
                                            : "bg-gray-300"
                                        } disabled:cursor-not-allowed disabled:opacity-50`}
                                        title={`Change option group to ${
                                          groupActive ? "Inactive" : "Active"
                                        }`}
                                      >
                                        <span
                                          className={`absolute top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-all ${
                                            groupActive ? "left-6" : "left-1"
                                          }`}
                                        >
                                          {groupBusy ? (
                                            <Loader2
                                              size={10}
                                              className="animate-spin text-gray-500"
                                            />
                                          ) : (
                                            <Power
                                              size={10}
                                              className={
                                                groupActive
                                                  ? "text-green-600"
                                                  : "text-gray-400"
                                              }
                                            />
                                          )}
                                        </span>
                                      </button>
                                    </div>

                                    {groupChange && (
                                      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-black/5 pt-2 text-[10px]">
                                        <span className="font-semibold text-gray-500">
                                          Last update:
                                        </span>
                                        <span
                                          className={`rounded px-1.5 py-0.5 font-bold ${
                                            groupChange.from === "Y"
                                              ? "bg-green-100 text-green-700"
                                              : "bg-red-100 text-red-600"
                                          }`}
                                        >
                                          {groupChange.from === "Y"
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                        <span className="font-bold text-gray-400">
                                          →
                                        </span>
                                        <span
                                          className={`rounded px-1.5 py-0.5 font-bold ${
                                            groupChange.to === "Y"
                                              ? "bg-green-100 text-green-700"
                                              : "bg-red-100 text-red-600"
                                          }`}
                                        >
                                          {groupChange.to === "Y"
                                            ? "Active"
                                            : "Inactive"}
                                        </span>
                                        <span className="ml-auto flex items-center gap-1 font-semibold text-green-700">
                                          <Check size={10} /> Updated
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {items.length > 0 && (
                                    <div className="mt-2 grid max-h-[180px] grid-cols-2 gap-1.5 overflow-y-auto pr-1 lg:grid-cols-2">
                                      {items.map((item, itemIndex) => {
                                        const itemId = getItemId(item);
                                        const itemActive =
                                          toText(
                                            item?.is_active ??
                                              item?.active_status ??
                                              item?.combo_is_active ??
                                              item?.mapping_is_active ??
                                              "Y",
                                          ).toUpperCase() !== "N";
                                        const itemBusy =
                                          statusBusyKey ===
                                          `item:${comboId}:${itemId}`;

                                        return (
                                          <div
                                            key={`${itemId}-${itemIndex}`}
                                            title={getItemName(item)}
                                            className={`flex min-w-0 items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium ${
                                              itemActive
                                                ? "bg-gray-50 text-gray-700"
                                                : "bg-red-50 text-red-500"
                                            }`}
                                          >
                                            <span className="min-w-0 flex-1 truncate">
                                              {getItemName(item)}
                                            </span>

                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleToggleComboItem(
                                                  comboId,
                                                  item,
                                                )
                                              }
                                              disabled={
                                                itemBusy ||
                                                comboConfigurableUpdating ||
                                                statusBusyKey !== ""
                                              }
                                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
                                                itemActive
                                                  ? "bg-green-100 text-green-700"
                                                  : "bg-red-100 text-red-600"
                                              } disabled:opacity-50`}
                                              title={`updateComboConfigurable - ${
                                                itemActive
                                                  ? "Deactivate"
                                                  : "Activate"
                                              }`}
                                            >
                                              {itemBusy ? (
                                                <Loader2
                                                  size={10}
                                                  className="animate-spin"
                                                />
                                              ) : (
                                                <Power size={10} />
                                              )}
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          EDIT COMBO MODAL -> updateComboOffer
         ===================================================== */}
      {editingCombo && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={handleUpdateCombo}
            className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Edit Combo
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Combo #{editingCombo?.combo_id} · updateComboOffer
                </p>
              </div>

              <button
                type="button"
                onClick={closeEditCombo}
                disabled={comboOfferUpdating || bhandareImageUploading}
                className="flex h-9 w-9 items-center justify-center rounded-full border text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <X size={17} />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Name
                  </label>
                  <input
                    value={editForm.combo_name}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        combo_name: event.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-lg border px-3 text-sm outline-none focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Price
                  </label>
                  <input
                    value={editForm.combo_price}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        combo_price: event.target.value,
                      }))
                    }
                    inputMode="decimal"
                    className="h-10 w-full rounded-lg border px-3 text-sm outline-none focus:border-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  Description
                </label>
                <textarea
                  value={editForm.combo_description}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      combo_description: event.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  Combo Image
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed bg-gray-50 p-3 hover:border-green-400">
                  <div className="flex h-20 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white">
                    {editImageData || editForm.combo_image ? (
                      <img
                        src={editImageData || editForm.combo_image}
                        alt="Combo"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageOff size={20} className="text-gray-300" />
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-700">
                      {editImageData ? "New image selected" : "Change image"}
                    </p>
                    <p className="mt-1 text-[10px] text-gray-500">
                      Original PNG/JPG/JPEG/WEBP is uploaded directly
                    </p>
                  </div>

                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                    onChange={handleEditImageChange}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700">
                  Active Status
                </label>
                <select
                  value={editForm.is_active}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      is_active: event.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={closeEditCombo}
                disabled={comboOfferUpdating || bhandareImageUploading}
                className="h-10 rounded-lg border bg-white px-4 text-xs font-bold text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={comboOfferUpdating || bhandareImageUploading}
                className="flex h-10 items-center gap-2 rounded-lg bg-green-600 px-4 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {(comboOfferUpdating || bhandareImageUploading) && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                {bhandareImageUploading
                  ? "Uploading..."
                  : comboOfferUpdating
                    ? "Updating..."
                    : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* =====================================================
          ITEM SELECTOR MODAL
         ===================================================== */}
      {showItemSelector && targetGroup && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Select Items
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {getGroupName(targetGroup)} · Group #{getGroupId(targetGroup)}
                </p>
              </div>

              <button
                type="button"
                onClick={closeItemSelector}
                className="flex h-9 w-9 items-center justify-center rounded-full border text-gray-500 hover:bg-gray-100"
              >
                <X size={17} />
              </button>
            </div>

            <div className="border-b bg-gray-50 p-4">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={itemSearchText}
                  onChange={(event) => setItemSearchText(event.target.value)}
                  placeholder="Search item..."
                  className="h-10 w-full rounded-lg border bg-white pl-9 pr-3 text-sm outline-none focus:border-green-500"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              {filteredSelectableItems.length === 0 ? (
                <div className="py-20 text-center text-sm text-gray-500">
                  No catalogue items found.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {filteredSelectableItems.map((item) => {
                    const itemId = getItemId(item);

                    const groupId = getGroupId(targetGroup);

                    const selected = asArray(groupSelections[groupId]).includes(
                      itemId,
                    );

                    const prices = getItemPrices(item);

                    return (
                      <button
                        key={itemId}
                        type="button"
                        onClick={() => toggleGroupItem(groupId, itemId)}
                        className={`flex min-h-[96px] gap-3 rounded-xl border p-3 text-left transition ${
                          selected
                            ? "border-green-500 bg-green-50 ring-1 ring-green-200"
                            : "border-gray-200 bg-white hover:border-green-300"
                        }`}
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-gray-50">
                          {getItemImage(item) ? (
                            <img
                              src={getItemImage(item)}
                              alt={getItemName(item)}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-gray-300">
                              <ImageOff size={18} />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-xs font-bold text-gray-900">
                                {getItemName(item)}
                              </p>

                              <p className="mt-0.5 text-[10px] text-gray-500">
                                Item #{itemId}
                                {item?.__categoryName
                                  ? ` · ${item.__categoryName}`
                                  : ""}
                              </p>
                            </div>

                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-bold ${
                                selected
                                  ? "border-green-600 bg-green-600 text-white"
                                  : "border-gray-300 bg-white text-transparent"
                              }`}
                            >
                              ✓
                            </span>
                          </div>

                          {prices.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {prices.slice(0, 3).map((price, priceIndex) => (
                                <span
                                  key={`${itemId}-${priceIndex}`}
                                  className="rounded bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-600"
                                >
                                  {price?.size ||
                                    price?.variantName ||
                                    "Default"}{" "}
                                  · {getPriceLabel(price)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t bg-gray-50 px-5 py-4">
              <p className="text-xs text-gray-500">
                {asArray(groupSelections[getGroupId(targetGroup)]).length}{" "}
                selected
              </p>

              <button
                type="button"
                onClick={closeItemSelector}
                className="h-10 rounded-lg bg-green-600 px-5 text-xs font-bold text-white hover:bg-green-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComboTab;
